import type { Messages } from '../catalogue.en'

/**
 * Schedule: the day view and an appointment's lifecycle.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const schedule: Partial<Messages> = {
  'schedule.selfBooking.title': 'קביעת תורים עצמאית',
  'schedule.selfBooking.doctor': 'רופא',
  'schedule.selfBooking.enabled': 'מותרת',
  'schedule.selfBooking.disabled': 'לא מותרת',
  'schedule.selfBooking.allowDoctor': 'לאפשר קביעת תור אצל {name}',
  'schedule.selfBooking.inactiveDoctor': 'רופא לא פעיל',
  'schedule.selfBooking.empty': 'אין רופאים זמינים',
  'schedule.selfBooking.saving': 'שמירת ההרשאה',
  'schedule.selfBooking.saved': 'ההרשאה נשמרה',
  'schedule.selfBooking.stale': 'ההרשאה השתנתה. יש לטעון מחדש לפני ניסיון נוסף.',
  'schedule.selfBooking.failed': 'לא ניתן לשמור או לטעון מחדש את ההרשאה.',
  'schedule.selfBooking.reload': 'טעינת הרשאות מחדש',
  'schedule.title': 'יומן',
  'schedule.today': 'היום',
  'schedule.invalidDate': 'התאריך אינו תקין',
  'schedule.invalidDateDetail': 'השתמש בפקדי התאריך כדי לבחור יום.',
  'schedule.columnPatient': 'מטופל',
  'schedule.openPatient': 'פתיחת {name}',
  'schedule.patientCode': 'קוד מטופל',
  'schedule.availability.available': 'זמין',
  'schedule.availability.noAvailability': 'אין זמינות לתיאום',
  'schedule.resourceType.practitioner': 'מטפל',
  'schedule.resourceType.patient': 'מטופל',
  'schedule.resourceType.room': 'חדר',
  'schedule.resourceType.equipment': 'ציוד',
  'schedule.resourceType.location': 'מיקום',
  'schedule.resourceType.organization': 'ארגון',
  'schedule.resourceType.servicePoint': 'נקודת שירות',
  'schedule.resourceType.other': 'אחר',
  'schedule.busyKind.appointment': 'תור',
  'schedule.busyKind.hold': 'שמירה',
  'schedule.busyKind.block': 'זמן חסום',
  'schedule.busyKind.busyElsewhere': 'עסוק במקום אחר',
  'schedule.lifecycle.held': 'נשמר',
  'schedule.lifecycle.planned': 'מתוכנן',
  'schedule.lifecycle.fulfilled': 'הושלם',
  'schedule.lifecycle.cancelled': 'בוטל',
  'schedule.lifecycle.noShow': 'לא הגיע',
  'schedule.lifecycle.enteredInError': 'הוזן בטעות',
  'schedule.dayStatus.planned': 'קרוב',
  'schedule.dayStatus.started': 'נרשם בקבלה',
  'schedule.dayStatus.completed': 'הושלם',
  'schedule.dayStatus.cancelled': 'בוטל',
  'schedule.dayStatus.noShow': 'לא הגיע',
  'schedule.candidateReason.earlier': 'אפשרות מוקדמת יותר',
  'schedule.candidateReason.sameSite': 'אותו אתר',
  'schedule.candidateReason.preferredResource': 'משאב מועדף',
  'schedule.candidateReason.smallerPractitionerGap': 'פער קטן יותר למטפל',
  'schedule.command.applied': 'בוצע',
  'schedule.command.replayed': 'כבר בוצע',
  'schedule.command.overlap': 'הזמן הזה חופף לפריט אחר ביומן.',
  'schedule.command.capacityExceeded': 'למשאב שנבחר אין קיבולת פנויה.',
  'schedule.command.outsideAvailability': 'הזמן שנבחר מחוץ לזמינות.',
  'schedule.command.revisionMismatch': 'היומן השתנה בזמן שהפעולה הייתה פתוחה.',
  'schedule.command.holdExpired': 'השמירה הזו פגה.',
  'schedule.command.forbidden': 'אין לך הרשאה לשנות את היומן הזה.',
  'schedule.command.invalidCommand': 'פעולת היומן הזו כבר אינה תקפה.',
  'schedule.command.missing': 'פריט היומן הזה כבר לא קיים. רענן את הלוח.',
  'schedule.command.rejected': 'פעולת היומן נדחתה.',
  'schedule.command.conflict': 'היומן השתנה לפני שהפעולה יכלה להתבצע.',
  'schedule.command.failed': 'לא ניתן היה להשלים את פעולת היומן.',
  'schedule.command.patientUnavailable': 'המטופל אינו זמין בזמן זה.',
  'schedule.command.outsidePatientStay': 'הזמן מחוץ לתקופת השהייה של המטופל.',

  'schedule.nav.label': 'חלקי לוח הזמנים',
  'schedule.nav.calendar': 'יומן',
  'schedule.nav.calendarDescription': 'תכנון תורים וזמנים חסומים',
  'schedule.nav.availability': 'זמינות',
  'schedule.nav.availabilityDescription': 'לוח שבועי ושינויים לפי תאריך',
  'schedule.nav.resources': 'משאבים',
  'schedule.nav.resourcesDescription': 'מקומות, ארגונים ומשאבים חיצוניים',
  'schedule.nav.visitTypes': 'סוגי מפגש',
  'schedule.nav.visit-typesDescription': 'משכים ומשאבים נדרשים',
  'schedule.find.action': 'חיפוש זמן',
  'schedule.block.action': 'חסימת זמן',
  'schedule.block.create': 'יצירת חסימה',
  'schedule.appointment.action': 'תור חדש',
  'schedule.appointment.create': 'יצירת תור',
  'schedule.appointment.visitReason': 'סיבת הביקור',

  'schedule.settings.loading': 'טוען הגדרות לוח זמנים',
  'schedule.settings.loadFailed': 'לא ניתן היה לטעון את הגדרות לוח הזמנים.',
  'schedule.settings.save': 'שמירה',
  'schedule.settings.cancel': 'ביטול',
  'schedule.settings.saveFailed': 'לא ניתן היה לשמור את השינוי.',
  'schedule.settings.editNamed': 'עריכת {name}',
  'schedule.settings.actions': 'פעולות',
  'schedule.settings.active': 'פעיל',
  'schedule.settings.inactive': 'לא פעיל',
  'schedule.resources.title': 'משאבים',
  'schedule.resources.branch': 'סניף',
  'schedule.branches.all': 'כל הסניפים',
  'schedule.branches.workplaceRequired': 'לפני הוספת שעות עבודה יש ליצור מקום עבודה פעיל לרופא בסניף זה.',
  'schedule.command.workplaceRequired': 'יש לבחור את מקום העבודה של הרופא לתור זה.',
  'schedule.command.workplaceUnavailable': 'מקום העבודה אינו זמין לרופא, לסניף או למועד התור שנבחרו.',
  'schedule.command.workplaceChanged': 'מקום העבודה השתנה. יש לרענן את הנתונים ולאשר את הבחירה.',
  'schedule.branches.overlap': 'שעות העבודה חופפות בין סניפים. יש לתקן את זמני הזמינות.',
  'schedule.branches.globalBlock': 'שעות אלה חופפות לחסימה החלה בכל הסניפים.',
  'schedule.branches.clearDescription': 'ניקוי שעות ושינויים לפי תאריך בסניף זה בלבד. חסימות כלליות, התחייבויות אישיות וסניפים אחרים יישארו ללא שינוי.',
  'schedule.resources.branchRequired': 'יש לבחור סניף',
  'schedule.resources.branchUnavailable': 'הסניף הזה אינו זמין עוד. יש לבחור סניף אחר.',
  'schedule.resources.branchInUse': 'יש להעביר או לבטל את התורים המשתמשים במשאב לפני שינוי הסניף שלו.',
  'schedule.resources.subtitle': 'מקומות, ארגונים, ציוד ומטפלים חיצוניים בעלי שם.',
  'schedule.resources.name': 'שם',
  'schedule.resources.type': 'סוג',
  'schedule.resources.location': 'מיקום',
  'schedule.resources.grid': 'מרווח',
  'schedule.resources.state': 'מצב',
  'schedule.resources.minutes': '{count} דק׳',
  'schedule.resources.preBuffer': 'חיץ לפני, דקות',
  'schedule.resources.postBuffer': 'חיץ אחרי, דקות',
  'schedule.resources.capabilities': 'יכולות',
  'schedule.resources.addTitle': 'הוספת משאב',
  'schedule.resources.editTitle': 'עריכת משאב',
  'schedule.resources.detailsTitle': 'פרטי משאב',
  'schedule.resources.openNamed': 'פתיחת המשאב {name}',
  'schedule.resources.empty': 'לא הוגדרו משאבים עצמאיים.',
  'schedule.resources.availabilityDefaults': 'ברירות מחדל לזמינות',
  'schedule.resources.address': 'כתובת',
  'schedule.resources.addressLine1': 'כתובת',
  'schedule.resources.addressLine2': 'פרטי כתובת',
  'schedule.resources.city': 'עיר',
  'schedule.resources.region': 'מחוז או אזור',
  'schedule.resources.postalCode': 'מיקוד',
  'schedule.resources.country': 'קוד מדינה',
  'schedule.resources.contact': 'פרטי קשר',
  'schedule.resources.phone': 'טלפון',
  'schedule.resources.email': 'אימייל',
  'schedule.resources.notes': 'הערות',
  'schedule.resources.metadata': 'מטא-נתונים (JSON)',
  'schedule.resources.booking': 'קביעת תורים',
  'schedule.availability.title': 'זמינות',
  'schedule.availability.subtitle': 'פרופילי זמינות למטפלים, מטופלים ומשאבים עצמאיים.',
  'schedule.availability.resource': 'משאב',
  'schedule.availability.target': 'יעד',
  'schedule.availability.timeZone': 'אזור זמן',
  'schedule.availability.useClinicTimeZone': 'שימוש באזור הזמן של המרפאה',
  'schedule.availability.useClinicCalendar': 'שימוש בלוח ימי העבודה של המרפאה',
  'schedule.availability.calendar': 'לוח ימי עבודה',
  'schedule.availability.calendarNone': 'לא הוחל',
  'schedule.availability.booking': 'קביעת תורים',
  'schedule.availability.bookable': 'ניתן לקביעה',
  'schedule.availability.openForBooking': 'פתוח לקביעת תורים',
  'schedule.availability.informational': 'למידע בלבד',
  'schedule.availability.profilesEmpty': 'לא הוגדרו פרופילי זמינות.',
  'schedule.availability.openNamed': 'פתיחת הזמינות של {name}',
  'schedule.availability.appointment': 'תור',
  'schedule.availability.busy': 'התחייבות אחרת',
  'schedule.availability.hold': 'שמור (ממתין לאישור)',
  'schedule.availability.dayAvailableHours': '{date}: זמין {hours}',
  'schedule.availability.dayClosed': '{date}: לא זמין',
  'schedule.availability.add': 'הוספה',
  'schedule.availability.menuExtra': 'הוספת שעות בתאריך זה',
  'schedule.availability.menuBlock': 'סימון כלא זמין',
  'schedule.availability.editDay': 'עריכת היום הזה',
  'schedule.availability.editDayTitle': 'שעות פתוחות ב־{date}',
  'schedule.availability.resetDay': 'חזרה ללוח השבועי',
  'schedule.availability.followsWeekly': 'היום הזה פועל לפי הלוח השבועי.',
  'schedule.availability.differsWeekly': 'היום הזה שונה מהלוח השבועי.',
  'schedule.availability.savePartlyApplied':
    'חלק מהיום הזה נשמר לפני שהשאר נדחה. הלוח נקרא מחדש, ולכן שמירה תכתוב רק את מה שעדיין חסר.',
  'schedule.availability.closedAllDay': 'סגור כל היום',
  'schedule.availability.addInterval': 'הוספת שעות',
  'schedule.availability.removeInterval': 'הסרת {hours}',
  'schedule.availability.legend': 'מקרא',
  'schedule.availability.legendAvailability': 'זמינות',
  'schedule.availability.legendOccupancy': 'זמן תפוס',
  'schedule.availability.usually': 'בדרך כלל: {pattern}',
  'schedule.availability.effectOpen': 'פתוח ב־{date}: {hours}',
  'schedule.availability.effectClosed': 'לא נותרו שעות פתוחות ב־{date}.',
  'schedule.availability.noEffect': 'השינוי הזה אינו משנה את השעות הפתוחות ב־{date}.',
  'schedule.availability.clear': 'ניקוי',
  'schedule.availability.restore': 'שחזור',
  'schedule.availability.presetSunThu': 'א׳–ה׳',
  'schedule.availability.presetMonFri': 'ב׳–ו׳',
  'schedule.availability.presetEveryDay': 'כל יום',
  'schedule.availability.notConfigured': 'הזמינות אינה מוגדרת.',
  'schedule.availability.createProfile': 'הגדרת זמינות',
  'schedule.availability.profileSettings': 'הגדרות זמינות',
  'schedule.availability.settingsHint': 'אזור הזמן ולוח העבודה הם הגדרות התזמון של {name} — לא הגדרת תצוגה',
  'schedule.availability.settingsOwnerNote': 'ההגדרות האלה שייכות ל־{name}: אזור הזמן ולוח העבודה חלים על כל מה שמתוזמן כאן — לא רק על תצוגת היומן הזו.',
  'schedule.availability.state': 'מצב',
  'schedule.availability.stateHint': 'מצב לא פעיל משעה את הפרופיל כולו: הוא אינו מציע שעות ולא ניתן לקבוע בו תור עד שיופעל שוב. הלוח השבועי והשינויים לפי תאריך נשמרים.',
  'schedule.availability.days': 'ימים',
  'schedule.availability.followWorkingCalendar': 'דילוג על ימי מנוחה בלוח שנבחר',
  'schedule.availability.day': 'יום',
  'schedule.availability.hours': 'שעות',
  'schedule.availability.openEnded': 'ללא תאריך סיום',
  'schedule.availability.reason': 'סיבה',
  'schedule.availability.block': 'לא זמין',
  'schedule.availability.extra': 'שעות שנוספו',
  'schedule.availability.start': 'התחלה',
  'schedule.availability.end': 'סיום',
  'schedule.availability.validFrom': 'בתוקף מ־',
  'schedule.availability.validTo': 'בתוקף עד',
  'schedule.availability.addRule': 'הוספת שעות שבועיות',
  'schedule.availability.editRule': 'עריכת שעות שבועיות',
  'schedule.availability.addExtraTitle': 'הוספת שעות בתאריך',
  'schedule.availability.editExtraTitle': 'עריכת שעות שנוספו',
  'schedule.availability.addBlockTitle': 'סימון זמן כלא זמין',
  'schedule.availability.editBlockTitle': 'עריכת זמן לא זמין',
  'schedule.availability.sunday': 'יום ראשון',
  'schedule.availability.monday': 'יום שני',
  'schedule.availability.tuesday': 'יום שלישי',
  'schedule.availability.wednesday': 'יום רביעי',
  'schedule.availability.thursday': 'יום חמישי',
  'schedule.availability.friday': 'יום שישי',
  'schedule.availability.saturday': 'שבת',
  'schedule.nav.workingCalendar': 'לוח ימי עבודה',
  'schedule.nav.working-calendarDescription': 'ימי עבודה וימי מנוחה',

  'schedule.canvas.scaleLabel': 'קנה מידה',
  'schedule.canvas.contextMenu': 'פעולות היומן',
  'schedule.canvas.scale.years': 'שנים',
  'schedule.canvas.scale.year': 'שנה',
  'schedule.canvas.scale.month': 'חודש',
  'schedule.canvas.scale.week': 'שבוע',
  'schedule.canvas.scale.day': 'יום',
  'schedule.canvas.fullDay': 'כל היממה',
  'schedule.canvas.nowLine': 'השעה הנוכחית',
  'schedule.canvas.nowLineHint': 'לצייר את השעה הנוכחית לפי שעון המרפאה כקו לרוחב המסלולים.',
  'schedule.canvas.navigation': 'ניווט בלוח השנה',
  'schedule.canvas.todayHint': 'מעבר להיום',
  'schedule.canvas.previousDay': 'היום הקודם',
  'schedule.canvas.nextDay': 'היום הבא',
  // The day on a phone: the two readings, the agenda's name and the picker.
  'schedule.handheld.view': 'תצוגה',
  'schedule.handheld.agenda': 'סדר יום',
  'schedule.handheld.board': 'לוח',
  'schedule.handheld.agendaLabel': 'סדר היום ל־{day}',
  'schedule.handheld.agendaEmpty': 'אין דבר ביום זה',
  'schedule.handheld.time': '{from}–{to}',
  'schedule.handheld.pickDate': 'מעבר לתאריך',
  'schedule.canvas.previousWeek': 'השבוע הקודם',
  'schedule.canvas.nextWeek': 'השבוע הבא',
  'schedule.canvas.previousMonth': 'החודש הקודם',
  'schedule.canvas.nextMonth': 'החודש הבא',
  'schedule.canvas.previousYear': 'השנה הקודמת',
  'schedule.canvas.nextYear': 'השנה הבאה',
  'schedule.canvas.previousYears': 'השנים הקודמות',
  'schedule.canvas.nextYears': 'השנים הבאות',
  'schedule.canvas.zoomLabel': 'רזולוציית זמן',
  'schedule.canvas.slotOption': '{minutes} דק׳',
  'schedule.canvas.zoomHint': 'גודל המרווח ברשת הזמן. אפשר לשנות גם ב־Ctrl+Shift+גלילה.',
  'schedule.canvas.range': '{from} – {to}',
  'schedule.canvas.bandName': '{from}–{to}, {label}',
  'schedule.canvas.monthGridLabel': 'הימים של {month}',
  'schedule.canvas.yearGridLabel': 'החודשים של {year}',
  'schedule.canvas.yearsGridLabel': 'השנים {from} עד {to}',
  'schedule.canvas.weekGridLabel': 'ימי השבוע',
  'schedule.canvas.dayGridLabel': 'שעות היום',
  'schedule.canvas.intervalTrack': 'ציר זמן עבור {date}',
  'schedule.canvas.openMonth': 'פתיחת {month}',
  'schedule.canvas.openYear': 'פתיחת {year}',
  'schedule.canvas.openWeek': 'פתיחת השבוע',
  'schedule.canvas.openDay': 'פתיחת היום',

  'schedule.timeObject.selected': 'אובייקט נבחר',
  'schedule.timeObject.newInterval': 'מרווח חדש',
  'schedule.timeObject.chooseType': 'יש לבחור מה משמעות המרווח. הסוג לעולם אינו מוסק מהרקע.',
  'schedule.timeObject.editTitle': 'עריכת אובייקט זמן',
  'schedule.timeObject.edit': 'עריכה',
  'schedule.timeObject.duplicate': 'שכפול',
  'schedule.timeObject.delete': 'מחיקה',
  'schedule.timeObject.deleteTitle': 'מחיקת אובייקט זמן',
  'schedule.timeObject.deleteAvailabilityTitle': 'להסיר את אובייקט הזמינות?',
  'schedule.timeObject.deleteAvailabilityDescription': 'הרשומה תושבת ותישמר בהיסטוריית הביקורת. ניתן לשחזר אותה דרך הרשומות.',
  'schedule.timeObject.type': 'סוג',
  'schedule.timeObject.intervalType': 'סוג המקטע',
  'schedule.timeObject.name': 'שם',
  'schedule.timeObject.duration': 'משך',
  'schedule.timeObject.recurrence': 'חזרה',
  'schedule.timeObject.recurrence.none': 'ללא חזרה',
  'schedule.timeObject.recurrence.daily': 'בכל יום',
  'schedule.timeObject.recurrence.weekly': 'מדי שבוע',
  'schedule.timeObject.kind.busy': 'זמן תפוס',
  'schedule.timeObject.kind.externalAppointment': 'פגישה במקום אחר',
  'schedule.timeObject.kind.travel': 'נסיעה',
  'schedule.timeObject.kind.transfer': 'העברה',
  'schedule.timeObject.kind.personal': 'אישי',
  'schedule.timeObject.kind.other': 'התחייבות אחרת',
  'schedule.timeObject.applyTo': 'החלת השינוי על',
  'schedule.timeObject.scope.occurrence': 'המופע הזה בלבד',
  'schedule.timeObject.scope.following': 'המופע הזה והמופעים הבאים',
  'schedule.timeObject.scope.series': 'הסדרה העתידית',
  'schedule.timeObject.ownerTimeZone': 'השעות המקומיות כפופות לאזור הזמן של הבעלים: {zone}.',
  'schedule.timeObject.overlapChooser': 'יש כאן אובייקטים חופפים. יש לבחור אובייקט להצגה:',
  'schedule.timeObject.snap': 'הצמדת עריכה',
  'schedule.timeObject.undo': 'ביטול ההזזה האחרונה',
  'schedule.timeObject.reverted': 'השרת דחה את השינוי. האובייקט הוחזר לזמן המאושר.',
  'schedule.timeObject.saveFailed': 'לא ניתן לשמור את אובייקט הזמן.',
  'schedule.timeObject.clearTitle': 'לנקות את תצורת היומן הניתנת לעריכה?',
  'schedule.timeObject.clearDescription': 'הפעולה משביתה שעות שבועיות, שינויי תאריך וסדרות התחייבות של בעלים זה.',
  'schedule.timeObject.clearRules': 'כללי זמינות שבועיים',
  'schedule.timeObject.clearOverrides': 'שינויים לתאריכים',
  'schedule.timeObject.clearSeries': 'סדרות התחייבות',
  'schedule.timeObject.clearKeepsAppointments': 'פגישות, שמירות והיסטוריית ביקורת אינן נמחקות.',
  'schedule.timeObject.copy': 'העתקה',
  'schedule.timeObject.cut': 'גזירה',
  'schedule.timeObject.paste': 'הדבקה כאן',
  'schedule.timeObject.addHere': 'הוספת מקטע כאן',
  'schedule.timeObject.newIntervalRange': '{date}, {from}–{to}',
  'schedule.timeObject.clearVisible': 'ניקוי המוצג',
  'schedule.timeObject.clearVisibleTitle': 'לנקות את הרשומות המתוארכות המוצגות כאן?',
  'schedule.timeObject.clearVisibleRange': '{from} – {to}',
  'schedule.timeObject.clearVisibleConfirm': 'ניקוי הרשומות',
  'schedule.timeObject.clearVisibleKeeps': 'שעות שבועיות והתחייבויות חוזרות הן רשומות קבועות ואינן מושפעות. תורים, שריונים והיסטוריית שינויים אינם נמחקים.',
  'schedule.timeObject.clearCommitments': 'התחייבויות חד־פעמיות',
  'schedule.timeObject.clearAll': 'ניקוי כל היומן',
  'schedule.timeObject.clearConfirm': 'ניקוי התצורה',
  'schedule.timePlanner.title': 'זמן לצוות',
  'schedule.timePlanner.subtitle': 'הצגת אנשים ומשאבים על משטח זמן אחד ומציאת חלון רציף שמתאים לכולם.',
  'schedule.timePlanner.searchOwners': 'חיפוש אנשים או משאבים',
  'schedule.timePlanner.owners': 'יומנים שנבחרו',
  'schedule.timePlanner.chooseOwners': 'יש לבחור יומן אחד או יותר להשוואה.',
  'schedule.timePlanner.loading': 'טעינת תצוגת היומנים',
  'schedule.timePlanner.loadFailed': 'לא ניתן לטעון את תצוגת היומנים.',
  'schedule.timePlanner.lane': 'אובייקטי זמן עבור {name}',
  'schedule.timePlanner.candidates': 'זמן פנוי לכולם',
  'schedule.timePlanner.searching': 'חיפוש זמן משותף',
  'schedule.timePlanner.searchFailed': 'לא ניתן לחשב זמן משותף.',
  'schedule.timePlanner.noCandidates': 'אין ביום זה חלון רציף שמתאים לכל היומנים שנבחרו.',
  'schedule.timePlanner.object.availability': 'זמין',
  'schedule.timePlanner.object.override': 'שינוי לתאריך',
  'schedule.timePlanner.object.commitment': 'התחייבות',
  'schedule.timePlanner.object.appointment': 'פגישה',
  'schedule.timePlanner.object.hold': 'שמירה',
  'schedule.timePlanner.object.constraint': 'מחוץ לתקופת הנסיעה',

  'schedule.workingCalendar.title': 'לוח ימי עבודה',
  'schedule.workingCalendar.subtitle': 'ימי עבודה במדינה או במחוז, בכל קנה מידה משנים ועד שעות.',
  'schedule.workingCalendar.calendar': 'לוח שנה',
  'schedule.workingCalendar.loading': 'טוען לוח ימי עבודה',
  'schedule.workingCalendar.empty': 'לא יובאו לוחות ימי עבודה.',
  'schedule.workingCalendar.emptyDetail': 'יש לייבא לוח מדינתי לפני שמגדירים עליו שעות עבודה של מרפאה או משאבים.',
  'schedule.workingCalendar.sourceNotice': 'נטען מ־{source} ({version}). שינויי ימים ידניים נשמרים בשירות התזמון.',
  'schedule.workingCalendar.readOnlyNotice': 'ניתן לצפות בלוח הזה, אך לא לערוך ימים ברמת המדינה.',
  'schedule.workingCalendar.status.working': 'יום עבודה',
  'schedule.workingCalendar.status.short': 'יום מקוצר',
  'schedule.workingCalendar.status.nonWorking': 'יום מנוחה',
  'schedule.workingCalendar.weekend': 'סוף שבוע',
  'schedule.workingCalendar.legend': 'מקרא',
  'schedule.workingCalendar.legendMarked': 'נקרא על ידי מקור או עריכה ידנית',
  'schedule.workingCalendar.daySummary': '{date}: {status}',
  'schedule.workingCalendar.daySummaryWithReason': '{date}: {status} — {reason}',
  'schedule.workingCalendar.monthSummary': '{working} ימי עבודה, {off} ימי מנוחה',
  'schedule.workingCalendar.markedCount': '{count} ימים מסומנים',
  'schedule.workingCalendar.status': 'סטטוס',
  'schedule.workingCalendar.reason': 'סיבה',
  'schedule.workingCalendar.reasonAction': 'סיבה…',
  'schedule.workingCalendar.reasonTitle': 'מדוע היום הזה שונה',
  'schedule.workingCalendar.cancel': 'ביטול',
  'schedule.workingCalendar.saveFailed': 'לא ניתן היה לשמור את היום בלוח ימי העבודה.',
  'schedule.visitTypes.open': 'ניהול סוגי מפגש בלוח הזמנים',

  'patientSchedule.title': 'זמינות המטופל',
  'patientSchedule.subtitle': 'התחייבויות מחוץ למרפאה נבדקות בכל בדיקת התנגשות.',
  'patientSchedule.start': 'התחלה',
  'patientSchedule.end': 'סיום',
  'patientSchedule.empty': 'לא נרשם זמן לא זמין.',

  'schedule.board.loading': 'טוען את הלוח',
  'schedule.board.loadFailed': 'לא ניתן היה לטעון את הלוח',
  'schedule.board.empty': 'אין שורות להצגה',
  'schedule.board.emptyDetail': 'לא הוגדרו משאבים ליום זה.',
  'schedule.board.legendLabel': 'מקרא',
  'schedule.board.legendBuffer': 'זמן חיץ',
  'schedule.board.legendLayers': 'שכבות',
  'schedule.board.legendActual': 'זמן בפועל',
  'schedule.board.legendNow': 'עכשיו',
  'schedule.legend.available': 'שעות הפעילות של הבעלים, מתחת לכל השאר במסלול: תורים נקבעים מעליהן.',
  'schedule.legend.extra': 'שעות שנוספו לשעות הרגילות ביום הזה, מצוירות כמו שעות פעילות אך בכחול.',
  'schedule.legend.block': 'שעות שהוסרו מהשעות הרגילות, בקווקוו אדום: לא נקבע עליהן דבר.',
  'schedule.legend.appointment': 'תור שנקבע: כרטיס מלא במסלול, עם שם המטופל ומצב התור.',
  'schedule.legend.hold': 'זמן שנשמר לתור שטרם אושר: מסגרת מקווקוות ללא מילוי.',
  'schedule.legend.busy': 'הבעלים עסוק במקום אחר: כרטיס אפור, כך שהזמן תפוס בלי תור כאן.',
  'schedule.legend.blockedTime': 'זמן חסום במסלול הזה: כרטיס אדום הנושא את הסיבה.',
  'schedule.legend.mutual': 'כל מי שנבחר בתכנון פנוי כאן: מסדרון מקווקו לרוחב כל המסלולים.',
  'schedule.legend.patientBusy': 'למטופל שנבחר יש משהו אחר בזמן הזה: קווקוו סגול לרוחב המסלולים.',
  'schedule.legend.buffer': 'זמן ההכנה והניקוי שמשאב שומר סביב תור: שוליים מנוקדים מעבר לכרטיס.',
  'schedule.legend.actual': 'מתי תור שהתחיל התקיים בפועל: פס לאורך קצה ההתחלה של המסלול.',
  'schedule.legend.now': 'שעון המרפאה כרגע: קו לרוחב כל המסלולים, עם השעה על הסרגל.',
  'schedule.board.mode': 'תצוגה',
  'schedule.board.modeReception': 'דלפק קבלה',
  'schedule.board.modeCanvas': 'קנבס',
  'schedule.board.modeHint': 'דלפק קבלה: עמודה לכל משאב, משבצות בצעד שנבחר בגובה קבוע, כרטיס לכל ביקור. קנבס: המסלולים החופשיים בכל קנה מידה.',
  'schedule.board.legendShow': 'הצגת המקרא',
  'schedule.board.legendHide': 'הסתרת המקרא',
  'schedule.reception.label': 'לוח דלפק הקבלה',
  'schedule.reception.slot': 'פנוי ב-{time}',
  'schedule.reception.arrived': 'במרפאה',
  'schedule.reception.inProgress': 'בטיפול',
  'schedule.reception.done': 'הסתיים',
  'schedule.reception.late': 'באיחור',
  'schedule.reception.lateBy': 'איחור של {minutes} דק׳',
  'schedule.reception.open': 'שעות קבלה',
  'schedule.reception.closed': 'סגור',
  'schedule.reception.legendCards': 'כרטיסי ביקור',
  'schedule.reception.legendTime': 'זמן',
  'schedule.reception.legendPlanned': 'ביקור שנקבע ואיש עוד לא סימן הגעה: כרטיס טורקיז, המטופל בהדגשה וסוג הביקור מתחת לשם.',
  'schedule.reception.legendArrived': 'המטופל במרפאה: כרטיס ירוק עם סימן וי בקצהו.',
  'schedule.reception.legendInProgress': 'הביקור התחיל: כרטיס כחול עם סימן דופק.',
  'schedule.reception.legendDone': 'הביקור הסתיים: כרטיס אפור עם לוח שנה מסומן.',
  'schedule.reception.legendLate': 'שעון המרפאה עבר את שעת ההתחלה ואיש לא סימן הגעה: כרטיס ענברי עם מילים באדום וסימן שעון, עם גודל האיחור.',
  'schedule.reception.legendNoShow': 'המטופל לא הגיע: כרטיס אדום עם סימן מחוק.',
  'schedule.reception.legendStripe': 'הפס בקצה ההתחלה של הכרטיס הוא צבע סוג הביקור, אותו צבע לסוג בכל עמודה.',
  'schedule.reception.legendOpen': 'המשאב מקבל: תאים לבנים, וכל תא פנוי נושא את השעה. לחיצה על תא קובעת את המשבצת.',
  'schedule.reception.legendClosed': 'מחוץ לשעות המשאב: תאים אפורים בלי שעה, אין מה לקבוע.',
  'schedule.reception.legendBlock': 'שעות שנסגרו ידנית בתוך המשמרת: קווקוו אדום עם הסיבה.',
  'schedule.board.conflict': 'התנגשות בתיאום',
  'schedule.board.patientUnavailable': 'המטופל אינו זמין',
  'schedule.board.holdUntil': 'שמירה עד {time}',
  'schedule.board.holdExpiredBadge': 'השמירה פגה',
  'schedule.board.lockedEvent': 'לא ניתן עוד להזיז את האירוע הזה.',
  'schedule.board.time': 'שעה',
  'schedule.board.resource': 'משאב',
  'schedule.board.visitType': 'סוג ביקור',
  'schedule.board.start': 'התחלה',
  'schedule.board.end': 'סיום',
  'schedule.board.snapHint': 'גרירה, מקשי החצים והקלדת שעה זזים בצעד הזה.',
  'schedule.board.pasteAt': 'הדבקה ב־{time}',
  'schedule.board.copied': '{name} הועתק. לחיצה ימנית על מסלול תדביק אותו שם.',
  'schedule.board.pasteRefused': 'אי אפשר להעתיק פריט זה לכאן: מה שהעתק צריך אינו גלוי בלוח הזה.',
  'schedule.board.cancelTitle': 'לבטל את התור הזה?',
  'schedule.board.cancelBlockTitle': 'להסיר את החסימה הזו?',
  'schedule.board.cancelDescription': 'הפריט יורד מהלוח. היסטוריית הביקורת נשמרת.',
  'schedule.board.keep': 'להשאיר',
  'schedule.board.createTitle': 'חדש בלוח הזמנים',
  'schedule.board.createKind': 'מה ליצור',
  'schedule.board.createRange': '{date}, {from}–{to}',
  'schedule.board.createAt': '{date}, {time}',
  'schedule.board.add': 'הוספת תור',
  'schedule.planner.hide': 'הסתרת המתכנן',
  'schedule.bandLabel': '{label}: {detail}',
  'schedule.calendar.lanesLabel': 'לוח יומי לפי משאב',
  'schedule.calendar.openEvent': 'פתיחת האירוע',
  'schedule.calendar.appointmentHere': 'תור חדש בשעה {time}',
  'schedule.calendar.blockHere': 'חסימת זמן בשעה {time}',
  'schedule.calendar.emptySelection': 'למשתתפים שנבחרו אין שורות לזימון ביום הזה.',
  'schedule.calendar.dayNote': 'תורים: {appointments}',
  'schedule.calendar.dayNoteConflicts': 'תורים: {appointments} · התנגשויות: {conflicts}',
  'schedule.calendar.busyOwner': '{owner}, {what}',
  'schedule.calendar.dayLabel': '{day}, {note}',
  'schedule.planner.title': 'תכנון משותף',
  'schedule.planner.objectTitle': '{from}–{to}, {name}',
  'schedule.planner.hint': 'בחרו אנשים, חדרים וציוד כדי להציג את הזמן שלהם על היומן ולמצוא חלונות פנויים משותפים.',
  'schedule.planner.selected': 'משתתפים שנבחרו',
  'schedule.planner.remove': 'הסרת {name}',
  'schedule.planner.clear': 'ניקוי הכול',
  'schedule.planner.search': 'חיפוש אנשים ומשאבים',
  'schedule.planner.typeFilter': 'סינון לפי סוג',
  'schedule.planner.loading': 'טוען את הקטלוג',
  'schedule.planner.loadFailed': 'לא ניתן היה לטעון את הקטלוג',
  'schedule.planner.noMatches': 'אין התאמות לחיפוש הזה.',
  'schedule.planner.capReached': 'אפשר להציג עד {count} משתתפים בו־זמנית.',
  'schedule.planner.chooseOwners': 'בחרו לפחות משתתף אחד כדי לראות זמן פנוי משותף.',
  'schedule.planner.monthHint': 'חלונות פנויים משותפים מוצגים בתצוגות השבוע והיום.',
  'schedule.planner.windows': 'זמן פנוי משותף',
  'schedule.planner.searching': 'מחפש זמן משותף',
  'schedule.planner.searchFailed': 'חיפוש הזמן המשותף נכשל',
  'schedule.planner.blocked': 'אין זמן פנוי משותף עבור: {names}',
  'schedule.planner.noWindows': 'אין חלונות פנויים משותפים בטווח הזה.',
  'schedule.planner.book': 'קביעת תור',
  'schedule.planner.mutual': 'כולם פנויים',
  'schedule.planner.projectionFailed': 'לא ניתן היה לטעון את יומני המשתתפים',
  'schedule.find.patient': 'מטופל',
  'schedule.find.type': 'סוג תור',
  'schedule.find.date': 'יום לחיפוש',
  'schedule.find.choose': 'בחירה…',
  'schedule.find.requirements': 'דרישות',
  'schedule.find.anyResource': 'כל משאב מתאים',
  'schedule.find.missingInput': 'יש לבחור מטופל וסוג תור ולציין סיבת ביקור כדי לחפש.',
  'schedule.find.patientAvailability': 'זמינות המטופל',
  'schedule.find.searching': 'מחפש חלונות פנויים',
  'schedule.find.failed': 'לא ניתן היה להשלים את החיפוש',
  'schedule.find.empty': 'אין חלונות מתאימים',
  'schedule.find.emptyDetail': 'נסו יום אחר או הרפו את בחירת המשאבים.',
  'schedule.find.candidates': 'זמנים מוצעים',
  'schedule.find.hold': 'שמירת {time}',
  'schedule.requirement.required': 'חובה',
  'schedule.requirement.preferred': 'מועדף',
  'schedule.requirement.contextOnly': 'הקשר בלבד',
  'schedule.panel.title': 'פריט יומן',
  'schedule.panel.loading': 'טוען את האירוע',
  'schedule.panel.loadFailed': 'לא ניתן היה לטעון את האירוע',
  'schedule.panel.planned': 'מתוכנן',
  'schedule.panel.actual': 'בפועל',
  'schedule.panel.arrived': 'הגעה {time}',
  'schedule.panel.markArrived': 'סימון הגעה',
  'schedule.panel.reservations': 'משאבים שמורים',
  'schedule.panel.buffers': 'זמני חיץ {pre} + {post} דק׳',
  'schedule.panel.holdExpires': 'השמירה תפוג ב-{time}',
  'schedule.panel.newStart': 'שעת התחלה חדשה',
  'schedule.panel.save': 'שמירת הזמן החדש',
  'schedule.panel.cancelEvent': 'ביטול תור',
  'schedule.panel.cancelBlock': 'ביטול חסימה',
  'schedule.panel.cancelReason': 'סיבת הביטול',
  'schedule.panel.confirmHold': 'אישור שמירה',
  'schedule.panel.releaseHold': 'שחרור שמירה',
  'schedule.atRisk.resourceBlocked': 'המשאב חסום',
  'schedule.atRisk.resourceInactive': 'המשאב אינו פעיל',
  'schedule.atRisk.availabilityChanged': 'הזמינות השתנתה',
  'schedule.atRisk.capacityConflict': 'התנגשות קיבולת',
}
